/**
 * Remix an agent (lane ALGOBOO2, REMIX1): pick one behaviour to change in plain words -- less chasing, smaller
 * positions, only the wallets you name -- see exactly which of its settings change and to what, name your remix, and
 * get a draft agent of your own on a fork of its strategy. Both keep their own record from today. Or change any setting
 * its program declares, each within its type (lane BOOUX1, A2-7); a new rule that is not a setting is written at the
 * desk, and the page says so by name.
 */
import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { DeclaredSetting, RemixKnob } from '@algoboo/schema/api'
import { Button, Card, EmptyState, Field, Input, Skeleton, Textarea, UiLink, cx, paths } from '@algoboo/ui'
import { isApiError, remixOptionsQuery, request, useRemixOptions } from '../lib/api'
import { usePageMeta } from '../lib/meta'
import { PageHeader } from '../components/Shell'
import { SignInPrompt, useSession } from '../components/session'
import { LimitNote } from '../components/LimitNote'
import { QueryError } from '../components/widgets'
import { prefetch } from '../lib/route-data'
import { attempt } from '../lib/attempt'

export const Route = createFileRoute('/a/$handle/remix')({
  loader: ({ context: { queryClient: qc }, params: { handle } }) => prefetch(qc.ensureQueryData(remixOptionsQuery(handle))),
  component: RemixPage,
})

const show = (v: unknown): string => (v === null || v === undefined || v === '' ? 'none' : typeof v === 'string' ? v : JSON.stringify(v))

/** A typed value of a declared setting from what was typed, or undefined when it is not one of its type. */
export function settingValue(d: Pick<DeclaredSetting, 'type'>, typed: string): unknown {
  const t = typed.trim()
  if (d.type === 'Bool') return t === 'true' ? true : t === 'false' ? false : undefined
  if (/^Exact\b/.test(d.type)) return /^-?[0-9]{1,38}$/.test(t) ? t : undefined
  if (/^Scalar\b/.test(d.type) || d.type === 'Duration') {
    const n = t === '' ? Number.NaN : Number(t)
    return Number.isFinite(n) && (d.type !== 'Duration' || n >= 0) ? n : undefined
  }
  if (d.type === 'Text' || d.type === 'Venue') return t.length > 0 ? t : undefined
  return undefined
}

function RemixPage() {
  const { handle } = Route.useParams()
  usePageMeta(`Remix @${handle}`)
  const { me, loading } = useSession()
  const q = useRemixOptions(handle)
  const navigate = useNavigate()
  const [knob, setKnob] = useState<RemixKnob | null>(null)
  const [wallets, setWallets] = useState('')
  const [typed, setTyped] = useState<Record<string, string>>({})
  const [newHandle, setNewHandle] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  if (q.isLoading || loading) return <Skeleton className="h-64 rounded-xl" />
  if (q.error || !q.data) return <QueryError error={q.error} refetch={() => void q.refetch()} compact={false} />
  const o = q.data
  const addresses = wallets
    .split(/[\s,]+/)
    .map((w) => w.trim().toLowerCase())
    .filter(Boolean)
  const walletsOk = addresses.length > 0 && addresses.length <= 20 && addresses.every((w) => /^0x[0-9a-f]{40}$/.test(w))
  const handleOk = /^[a-z0-9_]{3,24}$/.test(newHandle)
  const changed = Object.entries(typed).filter(([, v]) => v.trim() !== '')
  const settings = Object.fromEntries(changed.map(([n, v]) => [n, settingValue(o.settings.find((d) => d.name === n) ?? { type: '' }, v)]))
  const settingsOk = changed.length > 0 && Object.values(settings).every((v) => v !== undefined)
  const ready = knob !== null && handleOk && name.trim().length > 0 && (knob !== 'only_wallets' || walletsOk) && (knob !== 'settings' || settingsOk)
  const submit = async () => {
    if (!ready || !knob) return
    setBusy(true)
    setErr(null)
    await attempt(
      async () => {
        const r = await request('remixAgent', {
          params: { handle },
          body: {
            knob,
            handle: newHandle,
            name: name.trim(),
            ...(knob === 'only_wallets' ? { wallets: addresses as `0x${string}`[] } : {}),
            ...(knob === 'settings' ? { settings } : {}),
          },
        })
        void navigate({ to: '/a/$handle/manage', params: { handle: r.agent.handle } })
      },
      (x) => {
        setErr(isApiError(x) ? x.message : 'Could not make the remix. Try again.')
      },
    )
    setBusy(false)
  }
  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageHeader
        title={`Remix ${o.agent.name}`}
        lede={
          <>
            Change one thing about how{' '}
            <UiLink href={paths.agent(o.agent.handle)} className="text-accent hover:underline">
              @{o.agent.handle}
            </UiLink>{' '}
            trades and get your own agent. Both keep their own record from today.
          </>
        }
      />
      {o.unsupported ? null : !me ? (
        <SignInPrompt title="Sign in to remix" body="A remix is your own agent: sign in with your wallet to make one." />
      ) : (
        <Card className="space-y-5 p-5">
          <fieldset className="space-y-2" data-testid="remix-knobs">
            <legend className="text-[13px] font-semibold">What to change</legend>
            {o.knobs.map((k) => (
              <label key={k.knob} className={cx('block rounded-lg border p-3', knob === k.knob ? 'border-brand bg-brand-soft' : 'border-line', !k.available && 'opacity-60')} data-testid="remix-knob" data-knob={k.knob}>
                <span className="flex items-start gap-2">
                  <input type="radio" name="knob" disabled={!k.available} checked={knob === k.knob} onChange={() => setKnob(k.knob)} className="mt-1" />
                  <span>
                    <span className="block text-[14px] font-medium">{k.words}</span>
                    {k.changes.map((c) => (
                      <span key={c.name} className="block text-[12px] text-ink-3">
                        {c.words}: {show(c.now)} → {c.after === null ? 'what you name' : show(c.after)}
                      </span>
                    ))}
                    {!k.available && k.reason && <span className="block text-[12px] text-ink-3">{k.reason}</span>}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
          {knob === 'only_wallets' && (
            <Field label="The wallets it copies" htmlFor="remix-wallets" error={wallets && !walletsOk ? 'Up to 20 addresses, 0x and 40 hex digits each' : null}>
              <Textarea id="remix-wallets" rows={3} value={wallets} onChange={(e) => setWallets(e.target.value)} />
            </Field>
          )}
          {knob === 'settings' && (
            <fieldset className="space-y-2" data-testid="remix-settings">
              <legend className="text-[13px] font-semibold">Its settings</legend>
              {o.settings.map((d) => {
                const v = typed[d.name] ?? ''
                const bad = v.trim() !== '' && settingValue(d, v) === undefined
                return (
                  <Field key={d.name} label={`${d.words} (${d.type}${d.unit ? `, ${d.unit}` : ''})`} htmlFor={`remix-setting-${d.name}`} error={bad ? `a ${d.type}${d.type === 'Bool' ? ': true or false' : ''}` : null}>
                    <Input id={`remix-setting-${d.name}`} placeholder={`now ${show(d.now)}`} value={v} onChange={(e) => setTyped((t) => ({ ...t, [d.name]: e.target.value }))} data-testid="remix-setting" data-name={d.name} />
                  </Field>
                )
              })}
              <LimitNote limit={o.programLine} />
              <UiLink href={`/desk?agent=${encodeURIComponent(o.agent.handle)}`} className="text-[12px] text-accent hover:underline">
                Write a new rule at the desk
              </UiLink>
            </fieldset>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Its handle" htmlFor="remix-handle" error={newHandle && !handleOk ? '3 to 24 of a-z, 0-9 and _' : null}>
              <Input id="remix-handle" value={newHandle} onChange={(e) => setNewHandle(e.target.value.trim().toLowerCase())} />
            </Field>
            <Field label="Its name" htmlFor="remix-name">
              <Input id="remix-name" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
            </Field>
          </div>
          {err && (
            <p role="alert" className="text-[12.5px] text-danger">
              {err}
            </p>
          )}
          <div className="flex items-center justify-between gap-3">
            <p className="text-[12px] text-ink-3">Your remix starts as a draft: you activate it from its page. It trades with play money until live trading is switched on for it.</p>
            <Button variant="primary" disabled={!ready} loading={busy} onClick={() => void submit()} data-testid="remix-submit">
              Make my remix
            </Button>
          </div>
        </Card>
      )}
      {o.unsupported ? (
        <EmptyState compact title={`Remixing is unsupported until ${o.unsupported.owner}`} body={o.unsupported.reason} />
      ) : (
        o.knobs.every((k) => !k.available) && <EmptyState compact title="Nothing to remix here" body="This agent’s program has no setting a remix can change." />
      )}
    </div>
  )
}
